import angular from 'angular';
import _ from 'lodash-es';
import Card from '~/scripts/services/model/card';

/**
 * List of components not to include in the loop (in case of card.from > 1) because they already manage it
 *
 * @type {Set<string>}
 */
const componentsToExcludeFromLoop = new Set([
	'datagrid',
	'fileCurrent',
	'fileUpload',
	'form',
	'ktable',
	'mf',
	'repre',
	'wfCurrent',
	'wfHisto',
]);

/**
 * @param {string} position Position
 * @returns {boolean} Is label on the left?
 */
const labelOnTheLeft = position => [
	'left-left',
	'left-right',
].includes(position);

/**
 * @param {string} position Position
 * @returns {boolean} Is label on the right?
 */
const labelOnTheRight = position => [
	'right-left',
	'right-right',
].includes(position);

/**
 * @param {string} position Position
 * @returns {boolean} Is label on the left or right?
 */
const labelOnTheLeftOrRight = position => labelOnTheLeft(position) || labelOnTheRight(position);

/**
 * @param {string} position Position
 * @returns {string} Position
 */
const getLabelTextAlign = position => (position.match('-right') ? 'right' : 'left');

export default class KformComponent {
	/**
	 * @param {any} settings
	 */
	constructor(settings) {
		/** Common settings */
		this.clearOnHide = settings?.clearOnHide ?? false;
		this.hideLabel = settings?.hideLabel ?? false;
		this.hidden = settings?.hidden ?? false;
		this.input = false;
		this.isNew = settings?.isNew ?? false;
		this.key = settings?.key;
		this.label = settings?.label ?? '';
		this.labelMargin = settings?.labelMargin;
		this.labelPosition = settings?.labelPosition ?? 'top';
		this.labelWidth = settings?.labelWidth;

		/** @type {boolean | undefined} */
		this.lockKey = undefined;

		// this.tableView = settings?.tableView ?? false;
		this.type = settings?.type;

		/** @type {any} */
		this.validate = undefined;
	}

	/**
	 * @returns {any}
	 */
	export() {
		/** @type {any} */
		const component = _.cloneDeep(this);
		if (component.validate?.card instanceof Card) {
			component.validate.card = component.validate.card.toString();
		}
		else if (typeof component.validate?.card === 'object') {
			component.validate.card = `${component.validate.card.from}-${component.validate.card.to}`;
		}
		if (component.type === 'table' && 'rows' in component) {
			for (const row of component.rows) {
				for (const col of row) {
					col.components = col.components.map((/** @type {any} */ c2) => c2.export());
				}
			}
		}
		else if (component.type === 'columns' && 'columns' in component) {
			component.columns = component.columns.map(
				(/** @type {any} */ col) => ({
					...col,
					components: col.components.map(
						(/** @type {any} */ c2) => c2.export(),
					),
				}),
			);
		}
		else if (component.type === 'tabs' && 'tabs' in component) {
			for (const tab of component.tabs) {
				tab.components = tab.components.map((/** @type {any} */ c2) => c2.export());
			}
		}
		else if (component.type === 'mf') {
			component.options.name = undefined;
			component.options.multipleSelection = undefined;
		}
		else if ('components' in component) {
			component.components = component.components.map((/** @type {any} */ c2) => c2.export());
		}
		return {
			...component,
			isNew:     undefined,
			tableView: undefined,
		};
	}

	fixValidateCard() {
		if (this.validate?.card && !(this.validate.card instanceof Card)) {
			this.validate.card = new Card(this.validate.card);
		}
	}

	/**
	 * @returns {number} Label width
	 */
	getComponentLabelWidth() {
		return this.labelWidth ?? 30;
	}

	/**
	 * @returns {number} Label margin
	 */
	getComponentLabelMargin() {
		return this.labelMargin ?? 3;
	}

	/**
	 * @returns {any}
	 */
	getInputGroupStyles() {
		if (labelOnTheLeftOrRight(this.labelPosition)) {
			const totalLabelWidth = this.getComponentLabelWidth() + this.getComponentLabelMargin();

			/** @type {any} */
			const styles = {
				width: `${100 - totalLabelWidth}%`,
			};

			if (labelOnTheLeft(this.labelPosition)) {
				styles['margin-left'] = `${totalLabelWidth}%`;
			}
			else {
				styles['margin-right'] = `${totalLabelWidth}%`;
			}

			return styles;
		}

		if (this.type === 'panel') {
			return { display: 'block' };
		}

		return undefined;
	}

	/**
	 * @returns {any}
	 */
	getLabelStyles() {
		if (labelOnTheLeft(this.labelPosition)) {
			return {
				float:          'left',
				width:          `${this.getComponentLabelWidth()}%`,
				'margin-right': `${this.getComponentLabelMargin()}%`,
				'text-align':   getLabelTextAlign(this.labelPosition),
			};
		}

		if (labelOnTheRight(this.labelPosition)) {
			return {
				float:         'right',
				width:         `${this.getComponentLabelMargin()}%`,
				'margin-left': `${this.getComponentLabelMargin()}%`,
				'text-align':  getLabelTextAlign(this.labelPosition),
			};
		}

		if (this.type === 'datagrid' && this.labelPosition === undefined) {
			return {
				display:         'block',
				'margin-bottom': '-10px',
			};
		}

		return undefined;
	}

	/**
	 * @returns {Set<string>}
	 */
	getSubmissionKeys() {
		return new Set([
			'kid',
		]);
	}

	/**
	 * @param {any[]} newData
	 * @param {any[]} oldData
	 * @returns {boolean}
	 */
	hasChanged(newData = [], oldData = []) {
		const allowed = this.getSubmissionKeys();
		if (newData.length === 0 && oldData.length === 0) {
			return false;
		}

		if (allowed.size === 0) {
			return !angular.equals(newData, oldData);
		}

		return !_.isEqual(
			newData.map((/** @type {any}} */ d) => _.pickBy(d, (_v, k) => allowed.has(k))),
			oldData.map((/** @type {any}} */ d) => _.pickBy(d, (_v, k) => allowed.has(k))),
		);
	}

	/**
	 * @returns {boolean}
	 */
	isMultipleComponent() {
		this.fixValidateCard();
		return this.validate?.card?.isMultiple() && !componentsToExcludeFromLoop.has(this.type);
	}

	/**
	 * @param {any[]} [componentsToRequire]
	 * @returns {boolean}
	 */
	isRequired(componentsToRequire = undefined) {
		this.fixValidateCard();
		return (this.validate?.card?.from > 0 ?? false) || (
			Array.isArray(componentsToRequire) && componentsToRequire.includes(this.key)
		);
	}

	/**
	 * Determine if the label should be visible.
	 * @returns {boolean}
	 */
	labelVisible() {
		// @ts-ignore
		return this.type !== 'tabs' && (this.inDataGrid ? !!this.dataGridLabel : (this.label && !this.hideLabel));
	}
}
